import { Footer, FooterMobile, HeaderB2B, HeaderB2BMobile } from 'components';
import bannerSrc from '../../assets/images/webform-banner.svg';
import { useEffect, useState } from 'react';
import { postWebformToGoogleSheets } from 'apis/api/webform';
import { toast } from 'react-toastify';
import styles from './webform.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const Page = ({ isMobile = false }) => {
  const validInterest = ['rental', 'rent-to-own', 'beli-motor', 'partnership'];
  const navigate = useNavigate();
  const { interest } = useParams();

  useEffect(() => {
    // Check if interest param is valid
    if (!validInterest.includes(interest)) {
      // If not, redirect to the default interest
      navigate(`/saya-mau-electrum/${validInterest?.[0]}`, { replace: true });
    }
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerType, setCustomerType] = useState();
  const shouldShow = {
    app: ['rental', 'rent-to-own'].includes(interest),
    customerType: ['beli-motor', 'partnership'].includes(interest),
    companyName: customerType === 'Company',
    need: interest === 'partnership',
  };

  const dropdown = {
    interest: [
      {
        value: 'rental',
        label: 'Rental',
      },
      {
        value: 'rent-to-own',
        label: 'Sewa Milik Electrum',
      },
      {
        value: 'beli-motor',
        label: 'Beli Motor Electrum',
      },
      {
        value: 'partnership',
        label: 'Partnership',
      },
    ],
    app: ['', 'Gojek', 'Grab', 'Shopee', 'Maxim', 'InDrive', 'Others'],
    customerType: ['', 'Individual', 'Company'],
    needs: ['', 'Fleet Rental', 'Partnership'],
  };

  // Get label from dropdown.interest based on interest param
  const selectedInterestLabel =
    dropdown.interest.find((item) => item.value === interest)?.label || '';

  const handleInterestChange = (event) => {
    const selectedInterest = event.target.value;
    if (selectedInterest) {
      const searchParams = window.location.search;
      const url = `/saya-mau-electrum/${selectedInterest}${searchParams}`;
      window.location.assign(url);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    try {
      // Disable the submit button to prevent multiple clicks
      setIsSubmitting(true);

      const formData = new FormData(event.target);
      const data = Object.fromEntries(formData.entries());
      // Add timestamp to payload
      data.timestamp = new Date().toISOString();

      // Get utm_source from URL search params
      const searchParams = new URLSearchParams(window.location.search);
      const utmParams = ['utm_source', 'utm_medium', 'utm_campaign'];

      utmParams.forEach((param) => {
        // Check if the param exists in the URL
        const value = searchParams.get(param);

        // If it exists, add it to the data object
        if (value) data[param] = value;
      });

      const response = await postWebformToGoogleSheets(data);

      if (response.success) {
        const currentPath = window.location.pathname;
        // Redirect to success page
        window.location.assign(`${currentPath}/success`);
      } else {
        toast.error('Failed to submit form. Please try again later.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          'An error occurred while submitting the form.'
      );
    } finally {
      // Re-enable the submit button
      setIsSubmitting(false);
    }
  };

  const pageMeta = {
    title: `Saya Mau Electrum  - ${selectedInterestLabel}`,
    description:
      'Mau hemat pengeluaran hinga 40%? Sewa, beli, atau kerja sama bisnis, semua bisa dengan Electrum.',
    keywords: `electrum, pendaftaran, webform, motor listrik, daftar, ${interest}`,
  };

  return (
    <div>
      {isMobile ? <HeaderB2BMobile /> : <HeaderB2B />}

      <Helmet>
        <title>{pageMeta.title}</title>
        <meta name="title" content={pageMeta.title} />
        <meta name="description" content={pageMeta.description} />
        <meta name="keywords" content={pageMeta.keywords} />
        <meta property="og:title" content={pageMeta.title} />
        <meta property="og:description" content={pageMeta.description} />
        <link rel="canonical" href={`https://electrum.id/saya-mau-electrum/${interest}`} />
      </Helmet>

      <div className={styles['webform-container']}>
        {/* SEPEKAN BANNER */}
        <div style={{ width: '100%', marginBottom: '3rem' }}>
          <img
            loading="lazy"
            alt="Webform Banner"
            src={bannerSrc}
            style={{ width: '100%', borderRadius: '1.5rem', cursor: 'pointer' }}
          />
        </div>

        <h1
          style={{
            fontFamily: 'Gilroy-Bold',
            marginBottom: '2rem',
          }}
        >
          Isi Formulir & Tim Kami Akan Hubungi
        </h1>

        <form onSubmit={handleSubmit} style={{ fontFamily: 'Gilroy-Medium' }}>
          <div className={styles['form-group']}>
            <label htmlFor="interest" className={styles['label']}>
              Saya tertarik untuk
            </label>
            <select
              required
              name="interest"
              className={styles['select']}
              defaultValue={interest}
              onChange={handleInterestChange}
            >
              {dropdown.interest.map((interest) => (
                <option key={interest.value} value={interest.value}>
                  {interest.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="name" className={styles['label']}>
              Nama Lengkap
            </label>
            <input
              required
              name="name"
              type="text"
              placeholder="Masukkan nama lengkap Anda"
              className={styles['input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label htmlFor="phone" className={styles['label']}>
              No. HP (WA)
            </label>
            <input
              required
              name="phone"
              type="tel"
              placeholder="Masukkan no. HP Anda"
              className={styles['input']}
            />
          </div>

          {shouldShow.app && (
            <div className={styles['form-group']}>
              <label htmlFor="app" className={styles['label']}>
                Aplikasi Online
              </label>
              <select required name="app" className={styles['select']}>
                {dropdown.app.map((app) => (
                  <option key={app} value={app}>
                    {app}
                  </option>
                ))}
              </select>
            </div>
          )}

          {shouldShow.customerType && (
            <>
              <div className={styles['form-group']}>
                <label htmlFor="customerType" className={styles['label']}>
                  Individual/Company
                </label>
                <select
                  required
                  name="customer_type"
                  className={styles['select']}
                  onChange={(event) => setCustomerType(event.target.value)}
                >
                  {dropdown.customerType.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {shouldShow.companyName && (
                <div className={styles['form-group']}>
                  <label htmlFor="company_name" className={styles['label']}>
                    Nama Perusahaan
                  </label>
                  <input
                    required
                    name="company_name"
                    type="text"
                    placeholder="Masukkan nama perusahaan Anda"
                    className={styles['input']}
                  />
                </div>
              )}
            </>
          )}

          {shouldShow.need && (
            <div className={styles['form-group']}>
              <label htmlFor="need" className={styles['label']}>
                Kebutuhan
              </label>
              <select required name="need" className={styles['select']}>
                {dropdown.needs.map((need) => (
                  <option key={need} value={need}>
                    {need}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            className={styles['submit-button']}
            disabled={isSubmitting}
          >
            Hubungi Saya
          </button>
        </form>
      </div>

      {isMobile ? <FooterMobile /> : <Footer />}
    </div>
  );
};

export default Page;
